import type { UseQueryOptions } from "@tanstack/react-query";

import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import type { AlertGroup, AlertVariant } from "app/pages/activation-alerts/types/PlatformAlerts";

import type { BenchmarkGranularity } from "./common";

export type FirstPartyDataSource = "GA" | "SW";

interface ActivationAssets {
    benchmarkId: string;
    customIndustryId: string;
    keywordGroupsIds?: null | string[];
    rankTrackersIds?: null | string[];
    rankTrackerCampaignId?: string;
    alertId?: string;
    topics: string[];
}

export interface ActivationObjectSchema {
    accountId: string;
    id: string;
    userId: string;
    name?: string;
    sharedBy?: string;
    country: number;
    addedTime: string;
    lastUpdated: string;
    primaryDomain: string;
    competitorsDomains?: string[] | null;
    canEdit: boolean;
    isOwn: boolean;
    assets?: ActivationAssets;
    allDomains?: string[] | null;
    trafficSource?: FirstPartyDataSource;
}

export interface UpdateActivationParams {
    id: string;
    country: number;
    competitorsDomains?: string[] | null;
    primaryDomain: string;
    name?: string;
    trafficSource?: FirstPartyDataSource;
}

export interface CreateActivationParams {
    country: number;
    competitorsDomains: string[];
    primaryDomain: string;
    name?: string;
}

export interface GAGraphData {
    trend: {
        key: string;
        value: number;
    };
    absolute: number;
    change: number;
}

export interface GAPieData {
    desktop: number;
    mobile: number;
}

export interface GADataSchema {
    bounceRate: GAGraphData;
    deviceDistribution: GAPieData;
    pageViews: GAGraphData;
    pagesPerVisit: GAGraphData;
    totalVisits: GAGraphData;
    uniqueVisitors: GAGraphData;
    visitsDuration: GAGraphData;
}

export type TopicParams = {
    activationId: string;
    startDate: string;
    endDate: string;
    granularity: BenchmarkGranularity;
};

export type SuggestedCompetitors = {
    Domain: string;
    Favicon: string;
    Rank: number;
};

export type AICompetitorsSuggestion = {
    competitors: Array<{
        domain: string;
        reason: string;
        favicon: string;
    }>;
    isAiGenerated: boolean;
    threadId: string;
    suggestedPrompts?: Array<string>;
};

type ShareTrendSchema = {
    key: string;
    value: { value: number; change?: number };
};

export type ShortActivationSetSchema = {
    accountId: string;
    addedTime: string;
    allDomains: string[];
    assets: ActivationAssets;
    canEdit: boolean;
    competitorsDomains: string[];
    country: number;
    firstAvailableWeekDate: string;
    id: string;
    isFavorite: boolean;
    isOwn: boolean;
    lastUpdated: string;
    name: string;
    primaryDomain: string;
    sharedBy?: string;
    userId: string;
    hasNewAlerts?: boolean;
    trafficSource?: FirstPartyDataSource;
    readAlertsDate?: string;
};

export type ActivationSetSchema = Omit<ShortActivationSetSchema, "assets" | "firstAvailableWeekDate"> & {
    isSubscribed: boolean;
    share: number;
    periodOverPeriodShareDifference?: number;
    shareTrend: ShareTrendSchema[];
    trafficSource?: "GA" | "SW";
};

export type ProjectsResponseSchema = {
    activationSets: ActivationSetSchema[];
    totalNumber: number;
    isAllAvailableReturned: boolean;
} & {
    manuallyUpdated?: boolean;
};

type RankTrackerAssetRolesSettings = {
    userAssetRoles: Array<{
        userId: number;
        assetRole: number;
    }>;
};

export type RankTrackerCampaign = {
    id: string;
    name: string;
    site: string;
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    keywordCount: number;
    createdTime: string;
    updatedTime: string;
    /** First day with scraping data; omitted while scrape is pending. */
    firstScrapeTime?: string;
    /** Latest scraped day for this campaign from API; `null` while scrape is in progress. */
    lastScrapeDate: string | null;
    lastMigrationDate: string | null;
    competitors: string[];
    isActive: boolean;
    trackingScope: number;
    isEditable?: boolean;
    createdByUserId?: number;
    assetRolesSettings?: RankTrackerAssetRolesSettings;
    entities?: Record<string, string[]>;
    tags?: Record<string, unknown>;
    keywordToTargetUrls?: Record<string, unknown>;
};

export type RankTrackerCampaignsDTO = {
    sharedCampaigns: RankTrackerCampaign[];
    userCampaigns: RankTrackerCampaign[];
};

export type RankTrackerCountry = {
    name: string;
    code: number;
};

export type RTCampaignCreationDTO = {
    campaignName: string;
    devices: string[];
    competitors: string[];
    country: number;
};

export type KeywordTrendsCreationDTO = {
    campaignguid: string;
    from: string;
    to: string;
    sites: string;
    scrapingConfigurationGuids: string;
    isDaily: boolean;
    granularity?: string;
};

export type KeywordAvgCreationDTO = {
    campaignguid: string;
    from: string;
    to: string;
    sites: string;
    scrapingConfigurationGuids: string;
    isDaily: boolean;
    granularity?: string;
    invertedSerpFilter?: boolean;
    mainSiteSerpFilter?: boolean;
};

export type KeywordTrendsSchema = {
    movedToTopKeywordsLastDate: number;
    droppedFrom1: number;
    movedTop3: number;
    movedTop10: number;
    droppedToSecondPage: number;
    topKeywordsLastDate: number;
};

export type KeywordAvgDatedItem = {
    key: string;
    value: number;
};

export type KeywordAvgRecordTotal = {
    avgWeightPosition: number;
    visibility: number;
    clicks: number;
    shareOfVoiceIncludeOthers: number;
    shareOfVoiceExcludeOthers: number;
};

export type KeywordAvgRecord = {
    site: string;
    visibility: KeywordAvgDatedItem[];
    avgWeightPosition: KeywordAvgDatedItem[];
    clicks: KeywordAvgDatedItem[];
    shareOfVoiceIncludeOthers: KeywordAvgDatedItem[];
    shareOfVoiceExcludeOthers: KeywordAvgDatedItem[];
    total: KeywordAvgRecordTotal;
    change: KeywordAvgRecordTotal;
};

export type KeywordAvgSupportingData = {
    othersShareOfVoice: {
        data: KeywordAvgDatedItem[];
        total: number;
    };
};

export type KeywordAvgSchema = {
    records: KeywordAvgRecord[];
    supportingData: KeywordAvgSupportingData;
};

export type ActivationMetadataItem = {
    id: string;
    userId: number;
    accountId: number;
    country: number;
    name: string;
    primaryDomain: string;
    competitorsDomains: string[];
    isOwn: boolean;
    sharedBy?: string | null;
    lastUpdated: string;
    isFavorite: boolean;
    isSubscribed: boolean;
};

export type ActivationMetadata = {
    activations: ActivationMetadataItem[];
    totalNumber: number;
};

export type ValueChange = {
    value: number;
    change: number | null;
};

export type DatedValueTrendItem = {
    key: string;
    value: ValueChange;
};

export type TrafficWidgetResponse = {
    id: string | null;
    country: number;
    startDate: string | null;
    endDate: string | null;
    isGaConnected: boolean | null;
    share: number | null;
    periodOverPeriodShareDifference: number | null;
    shareTrend: DatedValueTrendItem[];
};

export type ShareOfVoiceWidgetResponse = {
    id: string | null;
    country: number | null;
    startDate: string | null;
    endDate: string | null;
    weightedAverageShareOfVoice: number | null;
    weightedAverageShareOfVoiceChange: number | null;
    decreasedTopicName: string | null;
    decreasedTopicShareOfVoiceChange: number | null;
    amountOfTopics?: number | null;
};

export type ShareOfVoiceQueryOptions = Omit<UseQueryOptions<ShareOfVoiceWidgetResponse>, "queryKey" | "queryFn">;

export enum PriorityAlertsStatus {
    Success = 1,
    NoAlerts,
    AllRead,
}

export type PriorityAlertItem = {
    alertType: AlertVariant;
    alertsGroup: AlertGroup;
    insight: string;
    tags: string[];
    totalAmount: number;
    domain: string;
    country: number;
    isRead: boolean;
    date: string;
    id: string;
};

export type PriorityAlerts = {
    status: PriorityAlertsStatus;
    priorityAlerts: PriorityAlertItem[];
};

export type ActionableInsight = {
    title: string;
    competitors: string[];
    tag: string;
    description: string;
    suggestion: string;
};

export type ActionableInsights = ActionableInsight[];

export type ActionableInsightsParams = {
    mainDomain: string;
    country: number;
    competitors: string[];
    activationId: string;
};
